import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

const AppTutorial: React.FC = () => {
    const theme = useTheme();
    const location = useLocation();
    const [run, setRun] = useState(false);
    const [steps, setSteps] = useState<Step[]>([]);
    const [storageKey, setStorageKey] = useState<string>('');

    // --- Steps Definitions ---

    const welcomeSteps: Step[] = [
        {
            target: 'body',
            content: (
                <div>
                    <h3>¡Bienvenido a RacerStad! 🏇</h3>
                    <p>
                        Tu plataforma de inteligencia artificial para carreras hípicas.
                        Te guiaremos brevemente por las funciones principales.
                    </p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '#hipodromo-selector-container',
            content: 'Aquí comienza la acción. Selecciona tu hipódromo favorito para ver las carreras programadas del día.',
        },
        {
            target: '#nav-user-profile',
            content: 'Accede a tu perfil para gestionar tu cuenta, ver tu historial y administrar tu suscripción.',
        },
         {
            target: 'body',
            content: (
                <div>
                    <h3>¡Listo para ganar! 🏆</h3>
                    <p>
                        Explora las carreras, consulta las predicciones de nuestra IA y toma las mejores decisiones.
                        ¡Buena suerte!
                    </p>
                </div>
            ),
            placement: 'center',
        },
    ];

    const hipodromoSteps: Step[] = [
         {
            target: 'body',
            content: (
                <div>
                    <h3>Vista de Hipódromo 🏟️</h3>
                    <p>
                        Aquí encontrarás toda la información detallada de las reuniones de carreras.
                    </p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '#hipodromo-races-list',
            content: 'Esta es la lista de las próximas jornadas. Selecciona una fecha para ver el detalle de cada carrera.',
        },
        {
            target: '#hipodromo-toggle-view',
            content: 'Usa este botón para alternar entre la lista de carreras y el ranking de los mejores del año.',
        },
    ];

    const topsSteps: Step[] = [
        {
            target: '#tops-title-mobile, #tops-title-desktop',
            content: 'Estos son los líderes indiscutibles de la temporada actual en este hipódromo.',
            disableBeacon: true,
        },
        {
            target: '#tops-mobile-tabs, #tops-desktop-table',
            content: 'Consulta el ranking actualizado de Ejemplares, Jinetes y Preparadores con más victorias.',
        }
    ];

    const raceDetailSteps: Step[] = [
        {
            target: '#race-selector-container',
            content: 'Navega rápidamente entre todas las carreras de la jornada usando este selector.',
            disableBeacon: true,
        },
        {
            target: '#race-view-tabs',
            content: 'Accede a diferentes herramientas: tabla de inscritos, historial anterior, y si tienes Premium, predicciones de IA y nuestro Agente experto.',
        },
        {
            target: '#race-content-panel',
            content: 'Aquí verás el detalle de los participantes. Analiza jinete, preparador, peso y partida.',
        }
    ];

    const raceAnteriorSteps: Step[] = [
        {
            target: '#race-tab-previous',
            content: 'Consulta el historial reciente de los competidores. Analiza sus últimas actuaciones para identificar tendencias.',
            disableBeacon: true,
        }
    ];

    const raceHandicapSteps: Step[] = [
        {
            target: '#race-tab-handicap',
            content: 'Visualiza la evolución del rendimiento de cada caballo a través de gráficos de handicap.',
            disableBeacon: true,
        }
    ];

    const raceStatsSteps: Step[] = [
        {
            target: '#race-tab-predictions',
            content: 'Accede a predicciones avanzadas generadas por nuestros modelos matemáticos y de Machine Learning.',
            disableBeacon: true,
        }
    ];

    const raceAgentSteps: Step[] = [
        {
            target: '#race-tab-agent',
            content: 'Interactúa con nuestra IA especializada. Pregúntale sobre las probabilidades, estrategias y análisis profundo de la carrera.',
            disableBeacon: true,
        }
    ];


    useEffect(() => {
        let currentSteps: Step[] = [];
        let key = '';
        let shouldRun = false;
        const queryParams = new URLSearchParams(location.search);
        const isTopsView = queryParams.get('view') === 'tops';
        const tabParam = queryParams.get('tab');

        // Lógica de rutas
        if (location.pathname === '/bienvenida' || location.pathname === '/') {
            key = 'tutorial_welcome_completed';
            currentSteps = welcomeSteps;
        } else if (location.pathname.includes('/carrera/')) {
            // Lógica de pestañas en Detalle de Carrera
            if (tabParam === 'anterior') {
                key = 'tutorial_race_anterior_completed';
                currentSteps = raceAnteriorSteps;
            } else if (tabParam === 'handicap') {
                key = 'tutorial_race_handicap_completed';
                currentSteps = raceHandicapSteps;
            } else if (tabParam === 'estadisticas') {
                key = 'tutorial_race_stats_completed';
                currentSteps = raceStatsSteps;
            } else if (tabParam === 'agente') {
                key = 'tutorial_race_agent_completed';
                currentSteps = raceAgentSteps;
            } else {
                // Default o 'actual'
                key = 'tutorial_race_detail_completed';
                currentSteps = raceDetailSteps;
            }

        } else if (location.pathname.startsWith('/hipodromo')) {
            if (isTopsView) {
                key = 'tutorial_tops_completed';
                currentSteps = topsSteps;
            } else {
                key = 'tutorial_hipodromo_completed';
                currentSteps = hipodromoSteps;
            }
        }

        if (key && currentSteps.length > 0) {
            const isCompleted = localStorage.getItem(key);
            if (!isCompleted) {
                shouldRun = true;
            }
        } else {
            shouldRun = false;
        }

        setStorageKey(key);
        setSteps(currentSteps);
        
        const timer = setTimeout(() => {
            setRun(shouldRun);
        }, 800);

        return () => clearTimeout(timer);
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            setRun(false);
            if (storageKey) {
                localStorage.setItem(storageKey, 'true');
            }
        }
    };

    if (steps.length === 0) return null;

    return (
        <Joyride
            steps={steps}
            run={run}
            continuous
            showSkipButton
            showProgress
            key={storageKey} 
            styles={{
                options: {
                    zIndex: 10000,
                    primaryColor: theme.palette.primary.main,
                    textColor: '#333',
                    backgroundColor: '#fff',
                },
                buttonNext: {
                    backgroundColor: theme.palette.primary.main,
                },
                buttonBack: {
                    color: theme.palette.primary.main,
                }
            }}
            callback={handleJoyrideCallback}
            locale={{
                back: 'Atrás',
                close: 'Cerrar',
                last: 'Finalizar',
                next: 'Siguiente',
                skip: 'Omitir',
            }}
        />
    );
};

export default AppTutorial;